import { DirectUploadProvider } from 'react-activestorage-provider'
import MediaPreview from 'Shared/components/MediaPreview'
import './FileUploader.scoped.scss'

export default function FileUploader({
  onUploaded,
  existingFilename,
  existingUrl,
  enablePreview = false,
  multiple = false,
  maxSize = null,
  inline = false,
  onError = () => {},
  allowedTypes = null,
  fileTypeError = null,
  isVolatile = false,
}) {
  const [filenames, setFilenames] = useState([])
  const fileInputRef = useRef(null)
  const [draggingOverTarget, setDraggingOverTarget] = useState(false)

  const handleClick = () => {
    fileInputRef.current.click()
  }

  const uploadedFilename = existingFilename || (existingUrl && existingUrl.split('/').pop())

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDraggingOverTarget(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDraggingOverTarget(false)
  }

  const validate = (files) => {
    if(maxSize) {
      for(const file of files) {
        if(file.size > maxSize) {
          onError('File size too large')
          return false;
        }
      }
    }

    if(allowedTypes) {
      for(const file of files) {
        if(!allowedTypes.includes(file.type)) {
          onError(fileTypeError || 'File type not allowed')
          return false;
        }
      }
    }

    return true
  }

  const acceptAttribute = allowedTypes ? allowedTypes.join(',') : undefined

  return <DirectUploadProvider
    directUploadsPath={ `/rails/active_storage/direct_uploads/${isVolatile ? 'amazon_volatile' : 'amazon'}` }
    onSuccess={(ids) => onUploaded(ids, multiple ? filenames : filenames[0])}
    render={({ handleUpload, uploads, ready }) => {
      const handleDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setDraggingOverTarget(false)
        if(validate(e.dataTransfer.files)) {
          setFilenames(Array.from(e.dataTransfer.files).map((f) => f.name))
          handleUpload(e.dataTransfer.files)
        }
      }

      return (
        <div className="image-uploader">
          <div className={`drag-target${draggingOverTarget ? ' active' : ''}${inline ? ' inline' : ''}`}
            onClick={handleClick}
            onDrop={handleDrop}
            onDragOver={handleDragEnter}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}>
            <SvgIconsUpload />
            <span>Drag and drop or click to upload</span>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            multiple={multiple}
            disabled={!ready}
            accept={acceptAttribute}
            onChange={e => {
              if(validate(e.currentTarget.files)) {
                setFilenames(Array.from(e.currentTarget.files).map((f) => f.name))
                handleUpload(e.currentTarget.files)
              }
            }
            }
          />

          {uploadedFilename &&
            <p className="no-margin-top">Existing file: {uploadedFilename}</p>
          }

          {enablePreview && existingUrl &&
            <MediaPreview url={existingUrl} />
          }

          {uploads.map(upload => {
            switch (upload.state) {
              case 'waiting':
                return <p key={upload.id}>Waiting to upload {upload.file.name}</p>
              case 'uploading':
                return (
                  <p key={upload.id}>
                    Uploading {upload.file.name}: {upload.progress}%
                  </p>
                )
              case 'error':
                return (
                  <p key={upload.id}>
                    Error uploading {upload.file.name}: {upload.error}
                  </p>
                )
              case 'finished':
                return (
                  <p key={upload.id}>Finished uploading {upload.file.name}</p>
                )
            }
          })}
        </div>
      )
    }}
  />
}

